// Client-safe `document.cookie` read helpers. All functions guard on
// `import.meta.client` and return safe defaults on the server so they can be
// called from anywhere (plugins, composables, components) without SSR crashes.
//
// No Vue/Nuxt imports — auto-imported by Nuxt from `app/utils/`.

/**
 * Parse `document.cookie` into `[name, value]` pairs.
 *
 * Module-private: callers should use `readCookie` / `readCookieByPrefix`, which
 * is where the SSR double guard lives exactly once.
 *
 * Values are returned **raw — never `decodeURIComponent`-ed**. Some consumers
 * forward cookie values to third parties byte-identical (e.g. Meta's `_fbc` /
 * `_fbp` click identifiers), and decoding a value that legitimately contains a
 * `%` would silently corrupt it with no error raised anywhere — broken
 * attribution is the only symptom. Adding decoding here is a plausible-looking
 * future change; don't. Decode at the call site if a caller ever needs it.
 */
function cookieEntries(): [string, string][] {
  if (!import.meta.client) return [];
  if (typeof document === 'undefined') return [];

  const cookies = document.cookie ? document.cookie.split('; ') : [];
  const entries: [string, string][] = [];
  for (const cookie of cookies) {
    // Split on the FIRST `=` only — base64-ish cookie values contain `=`
    // characters and must be returned whole. Never `split('=')`.
    const eqIdx = cookie.indexOf('=');
    if (eqIdx < 0) continue;
    entries.push([cookie.slice(0, eqIdx), cookie.slice(eqIdx + 1)]);
  }
  return entries;
}

/**
 * Raw value of the cookie with exactly this name, or `null` when absent.
 *
 * Duplicate cookie names can legitimately coexist across domain scopes (e.g.
 * `.arvee.io` and a subdomain both carrying `_fbp`). `document.cookie` returns
 * both with no guaranteed ordering and no scope information, so first-wins is a
 * known limitation with no fix available from JavaScript.
 */
export function readCookie(name: string): string | null {
  for (const [cookieName, value] of cookieEntries()) {
    if (cookieName !== name) continue;
    return value;
  }
  return null;
}

/**
 * Raw value of the **first** cookie whose name starts with `prefix`, or `null`
 * when none match.
 *
 * This deliberately returns the first prefix match even when its value looks
 * unusable — it must not skip ahead looking for a "better" later match.
 * Validation belongs to the caller.
 *
 * Same scope caveat as `readCookie`: duplicate prefix-matching cookies can
 * coexist across domain scopes (e.g. two `_pk_id.8.*` cookies from a parent
 * domain and a subdomain). `document.cookie` returns both with no guaranteed
 * ordering, so first-wins is a known limitation with no fix available from
 * JavaScript.
 */
export function readCookieByPrefix(prefix: string): string | null {
  for (const [cookieName, value] of cookieEntries()) {
    if (!cookieName.startsWith(prefix)) continue;
    return value;
  }
  return null;
}
